import { observer } from "mobx-react-lite";
import { Button, Dropdown, Grid, Message, Table } from "semantic-ui-react";
import MyPaging from "../../app/common/form/MyPaging";
import { Link } from "react-router-dom";
import ARPaymentForm from "./ARPaymentForm";
import { formatCurrency, getNegative } from "../../app/common/util/functions";
import { FormatNameUtils } from "../../app/common/util/formatName";
import { AccountsReceivableIndexItemDto, FilterByARIndex, QueueStatusDto, SearchARIndexDto } from "../../app/models/accountsReceivable";
import { Form, Formik } from "formik";
import MyTextInput from "../../app/common/form/MyTextInput";
import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { PagingParams } from "../../app/models/pagination";
import ARTicketSearch from "./ARTicketSearch";
import { useStore } from "../../app/stores/store";
import { arIndexFilterByOptions } from "../../app/common/options/option";
import LoadingComponent from "../../app/layout/LoadingComponent";


export default observer(function AccountsReceivableIndexResults() {
    const { t } = useTranslation(["common", "customer"]);

    const { modalStore, pricingStore, accountsReceivableIndexStore, accountsReceivableStore } = useStore();
    const {
        customerIdToLoad,
        loadPaymentTicketsModal,
        loadAccountsReceivablePayModal,
        loadingInitial: modalPaymentsLoading,
    } = accountsReceivableStore;

    const { dropdownPayTypesList } = pricingStore;
    const {
        loadingInitial,
        accountsReceivableIndexPagination,
        resetARIndexPredicate,
        accountsReceivableIndexPagingParams,
        setAccountsReceivableIndexPagingParams,
        loadARIndex,
        resetAccountsReceivableIndexRegistry,
        getAccountsReceivableIndex,
        setARIndexPredicate,
        expandedARIndexItem,
        setExpandedARIndexItem,
        loadingAccountsReceivableStatements,
        setInitiatePollingQueue,
        cantPrint,
        queuePrintAllAccountsReceivableStatement,
        printAllStatementQueueStatus,
        printAllAccountsReceivableStatements,
        setPrintAllStatementInitiated,
        setPrintAllStatementQueueStatus
    } = accountsReceivableIndexStore;

    const [expand, setExpand] = useState<boolean>(false);

    const [currentMessage, setCurrentMessage] = useState<{ message: string, error: Error | undefined }>({ message: '', error: undefined });
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const queueingIntervalId = useRef<NodeJS.Timeout | null>(null);

    const clearQueueingInterval = () => queueingIntervalId && queueingIntervalId.current ? clearInterval(queueingIntervalId.current) : null;

    useEffect(() => {

        const pollApi = () => {
            queuePrintAllAccountsReceivableStatement()
                .then((queueInfo: QueueStatusDto | undefined) => {
                    if (queueInfo?.status === 'processed') {
                        clearQueueingInterval();
                        setCurrentMessage({ message: t("printAllARStatementsCompletedMessage", { ns: "accountsReceivable" }), error: undefined });
                    }
                })
                .catch((error) => {
                    clearQueueingInterval();
                    console.error('API polling error:', error);
                    setCurrentMessage({ message: t("Network Error", { ns: "errors" }), error: error });
                })
        };

        if (isGenerating) {
            queueingIntervalId.current = setInterval(pollApi, 1000 * 5);
        }

        // Cleanup function to clear interval when component unmounts
        return () => {
            clearQueueingInterval();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGenerating]);

    useEffect(
        () => {
            loadARIndex();
        },
        [loadARIndex]
    );

    const handleExpand = (arItm: AccountsReceivableIndexItemDto) => (e: any) => {
        e.preventDefault();
        if (arItm.id === expandedARIndexItem?.id) {
            setExpand(!expand);
        }
        else {
            setExpandedARIndexItem(arItm);
            setExpand(true);
        }
    };


    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [active, setActive] = useState(false);

    function listOnClick(value: number) {
        if (value > 0 && accountsReceivableIndexPagination && value < accountsReceivableIndexPagination?.totalPages + 1) {
            setAccountsReceivableIndexPagingParams(new PagingParams(value, 25));
            resetAccountsReceivableIndexRegistry();
            loadARIndex();
        }
    }

    function handleFormSubmit(values: SearchARIndexDto) {
        setARIndexPredicate("customerName", values.customerName);
        setARIndexPredicate('filterBy', (values.filterBy ?? FilterByARIndex.All).toString());
        setAccountsReceivableIndexPagingParams(new PagingParams(1, 25));
        loadARIndex();
    }


    useEffect(() => {
        // eslint-disable-next-line eqeqeq
        if (printAllStatementQueueStatus && printAllStatementQueueStatus?.status == 'processed') {
            window.open(printAllStatementQueueStatus?.downloadUrl!, '_blank');
            setPrintAllStatementQueueStatus(undefined);
            setIsGenerating(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMessage, printAllStatementQueueStatus]);

    if (loadingInitial)
        return (
            <LoadingComponent
                content={t("accountsReceivableIndex_Loading", { ns: "accountsReceivable" })}
            />
        );

    return (
        <>
            {cantPrint && (
                <Message info>
                    {t("printAllARStatementsRestriction", { ns: "accountsReceivable" })}
                </Message>
            )}
            <Formik
                initialValues={{
                    customerName: "",
                    filterBy: FilterByARIndex.All as number
                } as SearchARIndexDto}
                onSubmit={(values) => handleFormSubmit(values)}
            >
                {({ handleSubmit, setFieldValue, values }) => (
                    <Form
                        className="ui form"
                        autoComplete="off"
                        onSubmit={handleSubmit}
                    >
                        <Grid stackable columns={3}>
                            <Grid.Row className="align-items-end">
                                <Grid.Column>
                                    <label>
                                        {t("filterBy", { ns: "common" })}:
                                    </label>
                                    <br />
                                    <Dropdown
                                        id="filterTicketsDropdown"
                                        button
                                        basic
                                        floating
                                        options={arIndexFilterByOptions}
                                        value={values.filterBy}
                                        onChange={(_, d: any) => {
                                            setFieldValue('filterBy', d.value);
                                        }}
                                    />
                                </Grid.Column>
                                <Grid.Column>
                                    <label>
                                        {t("customerOrCompanyName", { ns: "common" })}:
                                    </label>
                                    <MyTextInput
                                        placeholder={t("customer", {
                                            ns: "common",
                                        })}
                                        name="customerName"
                                        autoCapitalizeFirstLetter={true}
                                        maxLength={25}
                                    />
                                </Grid.Column>
                                <Grid.Column textAlign="right" >
                                    <Button
                                        content={t("batchPrint", { ns: "accountsReceivable", })}
                                        type="button"
                                        disabled={isGenerating || !!printAllStatementQueueStatus || loadingAccountsReceivableStatements}
                                        loading={isGenerating || !!printAllStatementQueueStatus || loadingAccountsReceivableStatements}
                                        onClick={() => {
                                            printAllAccountsReceivableStatements()
                                                .then(() => {
                                                    setInitiatePollingQueue(true);
                                                    setPrintAllStatementInitiated(true);
                                                })
                                                .finally(() => {
                                                    setIsGenerating(true);
                                                });
                                        }}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column>
                                    <Button
                                        color="green"
                                        className="save_button"
                                        icon="filter"
                                        content={t("buttons.search", { ns: "customer" })}
                                        type="submit"
                                        onClick={() => setActive(true)}
                                    />
                                    <Button
                                        color="grey"
                                        className="save_button"
                                        content={t("removeSearch", { ns: "customer" })}
                                        type="button"
                                        onClick={() => {
                                            resetARIndexPredicate();
                                            setActive(false);
                                            setFieldValue("customerName", undefined);
                                        }}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Form>
                )}
            </Formik>
            <Table striped unstackable className="dnxTable">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>
                            {t("customer", { ns: "common" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            {t("accountNumber", { ns: "accountsReceivable" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">
                            {t("creditLimit", { ns: "accountsReceivable" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">
                            {t("availCredit", { ns: "accountsReceivable" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">
                            {t("balanceDue", { ns: "accountsReceivable" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {getAccountsReceivableIndex && getAccountsReceivableIndex.length ?
                        getAccountsReceivableIndex.map((ar: AccountsReceivableIndexItemDto) => (
                            <React.Fragment key={ar.id}>
                                <Table.Row key={ar.id}>
                                    <Table.Cell
                                        className="hoveredLink"
                                        onClick={() => window.open(`/customer/${ar.customerId}`, "_blank")}
                                        data-label={`${t("name", { ns: "common" })}:`}
                                    >
                                        {ar.companyName ? ar.companyName : FormatNameUtils.customerDisplayName(ar?.customerFirstName, ar?.customerLastName, undefined)}
                                    </Table.Cell>
                                    <Table.Cell data-label={`${t("accountNumber", { ns: "accountsReceivable" })}:`}>
                                        {ar.accountNumber}
                                    </Table.Cell>
                                    <Table.Cell
                                        data-label={`${t("creditLimit", {
                                            ns: "accountsReceivable",
                                        })}:`}
                                        textAlign="right"
                                    >
                                        {formatCurrency(ar.creditLimit)}
                                    </Table.Cell>
                                    <Table.Cell
                                        data-label={`${t("availCredit", {
                                            ns: "accountsReceivable",
                                        })}:`}
                                        textAlign="right"
                                    >
                                        <span
                                            style={{ color: getNegative(ar.availableCredit) }}
                                        >
                                            {formatCurrency(ar.availableCredit)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell
                                        data-label={`${t("balanceDue", { ns: "accountsReceivable" })}:`}
                                        textAlign="right"
                                    >
                                        {formatCurrency(ar.balanceDue)}
                                    </Table.Cell>
                                    <Table.Cell className="textAlignRight" width={7}>
                                        <Button
                                            as={Link}
                                            to={`/accountsReceivableIndex`}
                                            size='small'
                                            icon="plus square"
                                            type="button"
                                            className="tealishButtons"
                                            content={t("seeTickets", { ns: "accountsReceivable" })}
                                            onClick={handleExpand(ar)}
                                        />
                                        <Button
                                            content={t("payAccount", { ns: "accountsReceivable" })}
                                            size='small'
                                            icon="dollar"
                                            color="green"
                                            disabled={modalPaymentsLoading || loadingAccountsReceivableStatements || isGenerating}
                                            // eslint-disable-next-line eqeqeq
                                            loading={ar.customerId == customerIdToLoad && modalPaymentsLoading}
                                            onClick={() => {
                                                loadPaymentTicketsModal(ar.customerId)
                                                    .then(() => loadAccountsReceivablePayModal(ar.customerId))
                                                    .then(() => {
                                                        modalStore.openWidestModal(
                                                            <ARPaymentForm
                                                                customerId={ar.customerId}
                                                                isAdd={true}
                                                                dropdownItems={dropdownPayTypesList}
                                                                amountDue={ar.balanceDue}
                                                                addItem={() => { }}
                                                            />
                                                        );
                                                    })
                                            }}
                                        />
                                        <Button
                                            content={t("customerdetails", { ns: "common" })}
                                            size='small'
                                            icon="clipboard list"
                                            as={Link}
                                            to={`/customer/${ar.customerId}`}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                                {expand && expandedARIndexItem?.id === ar.id && <ARTicketSearch customerId={expandedARIndexItem?.customerId ?? undefined} />}
                            </React.Fragment>
                        )) : (
                            <Table.Row key={"noItems"}>
                                <Table.Cell width="8" colSpan={8}>
                                    <Message
                                        color="yellow"
                                        className="textAlignCenter fontStyleItalic"
                                    >
                                        {t("grid_noItems", { ns: "common" })}
                                    </Message>
                                </Table.Cell>
                            </Table.Row>
                        )}
                </Table.Body>
                <Table.Footer>
                    <Table.Row>
                        <Table.Cell colSpan="7">
                            <MyPaging
                                itemCount={accountsReceivableIndexPagination ? accountsReceivableIndexPagination.totalItems : 25}
                                pageSize={accountsReceivableIndexPagingParams.pageSize}
                                currentPage={accountsReceivableIndexPagination ? accountsReceivableIndexPagination.currentPage : 1}
                                onClick={listOnClick}
                                onChange={listOnClick}
                                floated="right"
                            ></MyPaging>
                        </Table.Cell>
                    </Table.Row>
                </Table.Footer>
            </Table>
        </>
    );
});
